import React, { FC, useMemo } from 'react';
import classNames from 'classnames';
import {
  BasicContainer,
  BodyText,
  CustomBorderProps,
  FlexCol,
  FlexRow,
  H2Text,
  H3Text,
  ImageButton,
  GridDynamic,
} from '@minecraft.atoms';
import Icon from '@minecraft.icon';
import { Trans, useTranslation } from '@blocs.i18n';
import { DesignSystemTextColor } from '@minecraft.themes';
import { PlanType } from '../usePlans';
import { PlanFeatureType } from '../features';

export interface CompareFeaturesProps {
  plans: PlanType[];
  textColor?: DesignSystemTextColor;
}

const GridFeatureCell = ({
  isHeading,
  label,
  'data-testid': dataTestId,
  textColor = 'default',
}: {
  'data-testid': string;
  isHeading?: boolean;
  label?: string;
  textColor?: DesignSystemTextColor;
}) => {
  return (
    <BasicContainer data-testid={dataTestId}>
      <BodyText
        fontWeight={isHeading ? 'semibold' : 'regular'}
        color={isHeading ? 'statusDefault' : textColor}
        className="cn_atom_px-8"
      >
        {label && <Trans i18nKey={label} components={[<b />]} />}
      </BodyText>
    </BasicContainer>
  );
};
const GridPlanCellContainer: FC<{
  className?: string;
  plan: PlanType;
  children?: React.ReactNode;
  isHeading?: boolean;
  isFeature?: boolean;
  isLastFeature?: boolean;
  'data-testid': string;
  textColor?: DesignSystemTextColor;
}> = ({
  plan,
  className,
  children = null,
  isHeading,
  isFeature,
  isLastFeature,
  'data-testid': dataTestId,
  textColor = 'default',
}) => {
  let TextWrapper = BodyText;
  if (isHeading) {
    TextWrapper = H2Text;
  } else if (isFeature) {
    TextWrapper = H3Text;
  }

  const customBorder = {
    borderColor: `${plan.temperature}Paywall`,
    borderSize: 0.15,
    borderStyle: 'solid',
  } as CustomBorderProps;

  return (
    <BasicContainer
      className="cn_atom_p-0 cn_h-full cn_m-0 cn_overflow-hidden"
      customBordersRadius={{
        topLeft: isHeading ? 0.5 : 0,
        topRight: isHeading ? 0.5 : 0,
        bottomLeft: isLastFeature ? 0.3 : 0,
        bottomRight: isLastFeature ? 0.3 : 0,
      }}
      data-testid={dataTestId}
    >
      <BasicContainer
        padding={isLastFeature ? 1 : 4}
        w="full"
        m="auto"
        className={classNames(className, 'cn_h-full cn_atom_px-8')}
        borderColor={`${plan.temperature}Paywall`}
        borderSize={0.1}
        customBorders={{
          left: customBorder,
          right: customBorder,
          top: isHeading ? { ...customBorder, borderSize: 0.7, borderColor: `${plan.temperature}Paywall` } : null,
          bottom: isLastFeature ? { ...customBorder, borderColor: `${plan.temperature}Paywall` } : null,
        }}
        customBordersRadius={{
          topLeft: isHeading ? 0.5 : 0,
          topRight: isHeading ? 0.5 : 0,
          bottomLeft: isLastFeature ? 0.5 : 0,
          bottomRight: isLastFeature ? 0.5 : 0,
        }}
      >
        <TextWrapper fontWeight="semibold" color={textColor} align="center">
          {children}
        </TextWrapper>
      </BasicContainer>
    </BasicContainer>
  );
};

const PlanFeatureCell: FC<{
  feature?: PlanFeatureType;
  plan: PlanType;
  'data-testid': string;
  textColor?: DesignSystemTextColor;
}> = ({ plan, feature, 'data-testid': dataTestId, textColor = 'default' }) => {
  if (!feature) {
    return <span data-testid="placeholder-cell">&nbsp;</span>;
  }

  return feature.value ? (
    <Trans
      i18nKey={feature.translationKey}
      components={[<BodyText data-testid={dataTestId} align="center" lineHeight="ultraCondensed" color={textColor} />]}
      values={{ value: feature.value }}
    />
  ) : (
    <Icon name={plan.iconName ?? 'check-circle'} className="cn_mr-3" themeColor={`${plan.temperature}Paywall`} />
  );
};

function getAllFeaturesFromPlans(plans: PlanType[]): PlanFeatureType[] {
  const allFeatures: PlanFeatureType[] = [];
  plans.forEach((plan) => {
    plan.features.forEach((feature) => {
      if (
        !allFeatures.find((f) => f.code === feature.code) &&
        !plan.keyFeatures.find((keyFeature) => keyFeature.code === feature.code)
      ) {
        allFeatures.push(feature);
      }
    });
  });

  return allFeatures.sort((a, b) => a.order - b.order);
}

export const CompareFeatures: FC<CompareFeaturesProps> = React.memo(({ plans, textColor = 'default' }) => {
  const [isShown, setIsShown] = React.useState(false);
  const handleShow = () => setIsShown(!isShown);
  const { t } = useTranslation();
  const showablePlans = useMemo(() => plans.filter((plan) => !plan.shouldHidePlan), [plans]);
  const allFeatures = useMemo(() => getAllFeaturesFromPlans(showablePlans), [showablePlans]);

  const columns = showablePlans.map(() => '1fr');

  return (
    <BasicContainer
      data-testid="paywall-plans-compare-features"
      w={showablePlans.length > 1 ? 'full' : 'fit'}
      mt="4"
      mb={isShown ? '8' : '0'}
      className="cn_hidden cn_md:block"
    >
      <ImageButton onClick={handleShow} unsetSizing className="cn_w-full">
        <FlexCol className="cn_cursor-pointer" w="full">
          <FlexRow justifyContent="center" alignItems="center" gap="3" w="full">
            {showablePlans.length > 0 && (
              <H2Text align="center" color={textColor}>
                {t('common:paywall.plan.features.viewFeatures')}
              </H2Text>
            )}
            <Icon name={isShown ? 'chevron-open-thick' : 'chevron-closed-thick'} themeColor={textColor} />
          </FlexRow>
        </FlexCol>
      </ImageButton>
      <GridDynamic
        columns={`auto ${columns.join(' ')}`}
        gap="0"
        className="cn_mt-8 cn_atom_gap-x-4"
        alignItems="center"
        hidden={!isShown}
      >
        <GridFeatureCell
          data-testid="whats-included-header"
          isHeading
          label="common:paywall.plan.features.whatsIncluded"
        />
        {showablePlans.map((plan) => (
          <GridPlanCellContainer
            data-testid={`plan-title-${plan.billingCode}`}
            key={`${plan.billingCode}first`}
            plan={plan}
            isHeading
            textColor={textColor}
          >
            {plan.title}
          </GridPlanCellContainer>
        ))}
        {allFeatures.map((feature) => {
          return (
            feature.code && (
              <React.Fragment key={feature.code}>
                <GridFeatureCell
                  data-testid={`feature-label-${feature.code}`}
                  label={feature.customLabel ?? feature.label}
                  textColor={textColor}
                />
                {showablePlans.map((plan) => (
                  <GridPlanCellContainer
                    data-testid={`plan-cell-${plan.billingCode}-${feature.code}`}
                    plan={plan}
                    isFeature
                    key={plan.billingCode}
                    textColor={textColor}
                  >
                    <PlanFeatureCell
                      data-testid={`feature-checked-${feature.code}-${plan.billingCode}`}
                      feature={plan.features.find((pf) => pf.code === feature.code)}
                      plan={plan}
                      textColor={textColor}
                    />
                  </GridPlanCellContainer>
                ))}
              </React.Fragment>
            )
          );
        })}
        <GridFeatureCell data-testid="placeholder-cell" textColor={textColor} />
        {showablePlans.map((plan) => (
          <GridPlanCellContainer
            data-testid={`plan-cell-${plan.billingCode}-last`}
            key={`${plan.billingCode}last`}
            isLastFeature
            plan={plan}
            textColor={textColor}
          />
        ))}
      </GridDynamic>
    </BasicContainer>
  );
});
